// Filters/sort/page/mode <-> the `/influencer-explorer` URL's query string.
//
// InfluencerSection's filter state used to be plain useState with nothing
// backing it - so navigating to a card's profile screen and back (a real
// route change, not an in-page panel swap) unmounted the whole list and
// lost every filter, the app-search narrowing, the page you were on, and
// whether you were looking at "My Favourites". This is what fixes that:
// the address bar becomes the source of truth, so remounting after a "Back
// to Influencer Explorer" (InfluencerProfileSection's BackLink, wired to
// return to exactly the URL the card was clicked from - see InfluencerCard)
// rehydrates the same view instead of resetting to defaults. It also means
// a filtered/sorted view is copy-paste shareable, same rationale as the
// profile screen's own route.
//
// Param names mirror the API's own snake_case
// (TOP_INFLUENCER_FILTERS.md's worked examples, e.g.
// `?platform=youtube&creator_size=mega`) rather than this codebase's
// camelCase filter-state keys, so the address bar reads the same as the
// docs - same translation topInfluencerApi.js's FILTER_PARAM_MAP does for
// the actual API call, just also applied to the URL.

import { DEFAULT_FILTERS_INFLUENCER, INFLUENCER_MODE } from "./constants"

const FILTER_PARAM_MAP = {
  creatorSize: "creator_size",
  minFollowers: "min_followers",
  maxFollowers: "max_followers",
  minPosts: "min_posts",
  maxPosts: "max_posts",
  minTotalViews: "min_total_views",
  maxTotalViews: "max_total_views",
  minPostsAboutApps: "min_posts_about_apps",
  appId: "app_id",
  verifiedOnly: "verified_only",
  hasEmail: "has_email",
  hasLinks: "has_links",
  hasDescription: "has_description",
  excludePrivate: "exclude_private",
  mirroredImageOnly: "mirrored_image_only",
  discoveredWithinDays: "discovered_within_days",
  refreshedWithinDays: "refreshed_within_days",
  staleOnly: "stale_only",
  // search/username/country already spell the same both ways.
}

const REVERSE_FILTER_PARAM_MAP = Object.entries(FILTER_PARAM_MAP).reduce((acc, [camelKey, snakeKey]) => {
  acc[snakeKey] = camelKey
  return acc
}, {})

const BOOLEAN_FILTER_KEYS = new Set([
  "verifiedOnly", "hasEmail", "hasLinks", "hasDescription",
  "excludePrivate", "mirroredImageOnly", "staleOnly",
])
const ARRAY_FILTER_KEYS = new Set(["platform", "creatorSize"])

const RESERVED_PARAMS = new Set(["sort", "page", "mode", "app_name"])

const defaultSortFor = (mode) => (mode === INFLUENCER_MODE.FAVOURITES ? "saved" : "audience")

/**
 * { filters, sort, page, mode, appName } -> URLSearchParams. Only values
 * that differ from the default are written, so a plain, unfiltered visit to
 * `/influencer-explorer` keeps a clean URL rather than one full of `=`.
 */
export const buildInfluencerSearchParams = ({ filters, sort, page, mode, appName }) => {
  const params = new URLSearchParams()

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "" || value === false) return
    if (Array.isArray(value) && value.length === 0) return
    const paramKey = FILTER_PARAM_MAP[key] || key
    params.set(paramKey, Array.isArray(value) ? value.join(",") : String(value))
  })

  if (appName) params.set("app_name", appName)
  if (mode && mode !== INFLUENCER_MODE.ALL) params.set("mode", mode)
  if (sort && sort !== defaultSortFor(mode)) params.set("sort", sort)
  if (page && page !== 1) params.set("page", String(page))

  return params
}

/**
 * URLSearchParams -> { filters, sort, page, mode, appName }. Unknown or
 * malformed values fall back to defaults rather than throwing - same
 * "nothing here 400s" tolerance the filter endpoint itself has
 * (TOP_INFLUENCER_FILTERS.md §1), just applied client-side to a URL a user
 * could have hand-edited or landed on from an old link.
 */
export const parseInfluencerSearchParams = (searchParams) => {
  const filters = { ...DEFAULT_FILTERS_INFLUENCER }

  for (const [key, rawValue] of searchParams.entries()) {
    if (RESERVED_PARAMS.has(key)) continue
    const filterKey = REVERSE_FILTER_PARAM_MAP[key] || key
    if (!(filterKey in filters)) continue
    if (ARRAY_FILTER_KEYS.has(filterKey)) filters[filterKey] = rawValue.split(",").filter(Boolean)
    else if (BOOLEAN_FILTER_KEYS.has(filterKey)) filters[filterKey] = rawValue === "true" || rawValue === "1"
    else filters[filterKey] = rawValue
  }

  const mode = searchParams.get("mode") === INFLUENCER_MODE.FAVOURITES ? INFLUENCER_MODE.FAVOURITES : INFLUENCER_MODE.ALL
  const sort = searchParams.get("sort") || defaultSortFor(mode)
  const pageRaw = parseInt(searchParams.get("page"), 10)
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1
  const appName = searchParams.get("app_name") || null

  return { filters, sort, page, mode, appName }
}
